import {
  AIModelConfig,
  AIProbe,
  AIProvider,
  AIProviderConfig,
  AIProviderId,
  AISettings,
  AITestErrorCode,
} from "@dashboard-v3/api";

export type HeaderRow = { name: string; value: string };

export type FormState = {
  provider: string;
  secret: string;
  baseUrl: string;
  model: string;
  maxOutputTokens: string;
  reasoning: string;
  temperature: string;
  maxInputTokens: string;
  extraParams: string;
  headers: HeaderRow[];
};

export type FormErrors = Partial<Record<keyof FormState, string>>;

export type Outcome = {
  mode: "test" | "save";
  ok: boolean;
  code?: string;
  probe?: Partial<AIProbe>;
};

export function emptyForm(provider: AIProvider): FormState {
  return {
    provider: provider.id,
    secret: "",
    baseUrl: "",
    model: provider.defaultModel || "",
    maxOutputTokens: "",
    reasoning: "",
    temperature: "",
    maxInputTokens: "",
    extraParams: "",
    headers: [],
  };
}

/**
 * `secret` starts empty: the API returns the stored key masked, so the field
 * only holds a key the user types.
 */
export function toForm(settings: AISettings): FormState {
  const providerId = settings.defaultModel.provider;
  const config = settings.providers?.[providerId];
  const model = config?.models?.[0];
  const headers = Object.entries(config?.extraHeaders || {});

  return {
    provider: providerId,
    secret: "",
    baseUrl: config?.baseUrl || "",
    model: model?.model || settings.defaultModel.model || "",
    maxOutputTokens: toText(model?.maxOutputTokens),
    reasoning: model?.reasoning || "",
    temperature: toText(model?.temperature),
    maxInputTokens: toText(model?.maxInputTokens),
    extraParams: toJsonText(model?.extraParams),
    headers: headers.map(([name, value]) => ({ name, value })),
  };
}

export function getStoredSecret(
  settings: AISettings | null,
  providerId: string
) {
  return settings?.providers?.[providerId as AIProviderId]?.secret || "";
}

/**
 * Empty fields are left out of the record, never sent as null. Without a typed
 * key, `secret` is left out too and the API keeps the stored one.
 */
export function toSettings(form: FormState, provider: AIProvider): AISettings {
  const secret = form.secret.trim();
  const model = form.model.trim();
  const reasoning = form.reasoning.trim();
  const baseUrl = form.baseUrl.trim();
  const headers = form.headers.filter(header => header.name.trim());

  const modelConfig: AIModelConfig = {
    model,
    ...(form.maxOutputTokens !== "" && {
      maxOutputTokens: Number(form.maxOutputTokens),
    }),
    ...(reasoning && { reasoning }),
    ...(form.temperature !== "" && { temperature: Number(form.temperature) }),
    ...(form.maxInputTokens !== "" && {
      maxInputTokens: Number(form.maxInputTokens),
    }),
    ...(form.extraParams.trim() && {
      extraParams: JSON.parse(form.extraParams),
    }),
  };

  const providerConfig: AIProviderConfig = {
    ...(secret && { secret }),
    ...(provider.baseUrlRequired && baseUrl && { baseUrl }),
    ...(headers.length > 0 && {
      extraHeaders: Object.fromEntries(
        headers.map(header => [header.name.trim(), header.value])
      ),
    }),
    models: [modelConfig],
  };

  return {
    defaultModel: { provider: form.provider as AIProviderId, model },
    providers: {
      [form.provider]: providerConfig,
    } as AISettings["providers"],
  };
}

export function getFormErrors(form: FormState): FormErrors {
  const errors: FormErrors = {};

  if (!isIntegerFrom(form.maxOutputTokens, 1)) {
    errors.maxOutputTokens = "externalAI.form.errors.minOne";
  }

  if (!isIntegerFrom(form.maxInputTokens, 1)) {
    errors.maxInputTokens = "externalAI.form.errors.minOne";
  }

  if (form.temperature !== "" && !(Number(form.temperature) >= 0)) {
    errors.temperature = "externalAI.form.errors.minZero";
  }

  if (form.extraParams.trim() && !isJsonObject(form.extraParams)) {
    errors.extraParams = "externalAI.form.errors.extraParams";
  }

  return errors;
}

export function isComplete(
  form: FormState,
  provider: AIProvider,
  hasStoredSecret: boolean
) {
  const hasSecret = hasStoredSecret || Boolean(form.secret.trim());
  const hasBaseUrl = !provider.baseUrlRequired || Boolean(form.baseUrl.trim());

  return hasSecret && Boolean(form.model.trim()) && hasBaseUrl;
}

export function isSameForm(a: FormState | null, b: FormState | null) {
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

export function countAdvanced(form: FormState) {
  const fields = [
    form.maxOutputTokens,
    form.reasoning,
    form.temperature,
    form.maxInputTokens,
    form.extraParams,
  ];
  const filled = fields.filter(value => value.trim() !== "").length;
  const headers = form.headers.filter(header => header.name.trim()).length;

  return filled + headers;
}

const TEST_ERROR_CODES: AITestErrorCode[] = [
  "AI_PROVIDER_INVALID_KEY",
  "AI_PROVIDER_MODEL_NOT_FOUND",
  "AI_PROVIDER_RATE_LIMITED",
  "AI_PROVIDER_UNAVAILABLE",
  "AI_PROVIDER_REJECTED",
  "AI_PROVIDER_UNREACHABLE",
  "AI_PROVIDER_INVALID_BASE_URL",
  "AI_PROVIDER_INVALID_CONFIG",
  "AI_PROVIDER_SECRET_REQUIRED",
];

export function getTestErrorKey(code?: string) {
  const isKnown = TEST_ERROR_CODES.includes(code as AITestErrorCode);
  const suffix = isKnown ? code : "UNKNOWN";

  return `externalAI.testErrors.${suffix}`;
}

export function formatTokens(amount: number) {
  if (amount >= 1e6) return `${trimZeros((amount / 1e6).toFixed(2))}M`;
  if (amount >= 1e3) return `${trimZeros((amount / 1e3).toFixed(1))}K`;
  return String(amount);
}

function normalize(form: FormState | null) {
  if (!form) return null;

  return {
    ...form,
    headers: form.headers.filter(header => header.name.trim() || header.value),
  };
}

function toText(value?: number) {
  if (value === undefined || value === null) return "";
  return String(value);
}

function toJsonText(value?: Record<string, unknown>) {
  if (!value || Object.keys(value).length === 0) return "";
  return JSON.stringify(value, null, 2);
}

function trimZeros(value: string) {
  return value.replace(/\.?0+$/, "");
}

function isIntegerFrom(value: string, min: number) {
  if (value === "") return true;
  const number = Number(value);

  return Number.isInteger(number) && number >= min;
}

function isJsonObject(text: string) {
  try {
    const value = JSON.parse(text);
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
  } catch (error) {
    return false;
  }
}
